import axios from 'axios';
import type {
  EmailTemplateDto,
  EmailTemplateDetailDto,
  EmailTemplateSaveRequest,
  PlaceholderGroup,
} from '../types/email-template';

const getHeaders = (token?: string) => ({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

// ── List templates ──────────────────────────────────────

export async function getEmailTemplates(
  token?: string,
  templateType?: string
): Promise<EmailTemplateDto[]> {
  const params = templateType ? `?templateType=${templateType}` : '';
  const res = await axios.get(`/api/EmailTemplate${params}`, getHeaders(token));
  return res.data;
}

// ── Get single template (with design JSON) ──────────────

export async function getEmailTemplateById(
  oid: string,
  token?: string
): Promise<EmailTemplateDetailDto> {
  const res = await axios.get(`/api/EmailTemplate/${oid}`, getHeaders(token));
  return res.data;
}

// ── Create template ─────────────────────────────────────

export async function createEmailTemplate(
  request: EmailTemplateSaveRequest,
  token?: string
): Promise<{ Oid: string; Message: string }> {
  const res = await axios.post('/api/EmailTemplate', request, getHeaders(token));
  return res.data;
}

// ── Update template ─────────────────────────────────────

export async function updateEmailTemplate(
  oid: string,
  request: EmailTemplateSaveRequest,
  token?: string
): Promise<{ Message: string }> {
  const res = await axios.put(`/api/EmailTemplate/${oid}`, request, getHeaders(token));
  return res.data;
}

// ── Delete template ─────────────────────────────────────

export async function deleteEmailTemplate(
  oid: string,
  token?: string
): Promise<{ Message: string }> {
  const res = await axios.delete(`/api/EmailTemplate/${oid}`, getHeaders(token));
  return res.data;
}

// ── Duplicate template ──────────────────────────────────

export async function duplicateEmailTemplate(
  oid: string,
  token?: string
): Promise<{ Oid: string; Message: string }> {
  const res = await axios.post(`/api/EmailTemplate/${oid}/duplicate`, null, getHeaders(token));
  return res.data;
}

// ── Set as default ──────────────────────────────────────

export async function setDefaultEmailTemplate(
  oid: string,
  token?: string
): Promise<{ Message: string }> {
  const res = await axios.post(`/api/EmailTemplate/${oid}/set-default`, null, getHeaders(token));
  return res.data;
}

// ── Template types ──────────────────────────────────────

export async function getEmailTemplateTypes(token?: string): Promise<string[]> {
  const res = await axios.get('/api/EmailTemplate/types', getHeaders(token));
  return res.data;
}

// ── Placeholders ────────────────────────────────────────

export async function getEmailPlaceholders(
  token?: string,
  templateType?: string
): Promise<PlaceholderGroup[]> {
  const params = templateType ? `?templateType=${templateType}` : '';
  const res = await axios.get(`/api/EmailTemplate/placeholders${params}`, getHeaders(token));
  return res.data;
}
